import { useMutation } from '@apollo/client';
import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import { DeleteObligationChangeAttestationDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import {
  type DeleteObligationChangeAttestationCompatibilityResult,
  type DeleteObligationChangeAttestationTRPCInput,
  useDeleteObligationChangeAttestationTRPC,
} from './useDeleteObligationChangeAttestationTRPC';

export const useDeleteObligationChangeAttestation = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');
  const { user } = useRisksmartUser();

  // GraphQL mutation
  // COI-577: moved off the detail page so every consumer gets it.
  const [deleteAttestationGraphQL, graphqlState] = useMutation(
    DeleteObligationChangeAttestationDocument,
    { update: (cache) => evictField(cache, 'obligation_change') }
  );

  // tRPC mutation
  const trpcMutation = useDeleteObligationChangeAttestationTRPC();

  const deleteObligationChangeAttestation = async (
    variables: DeleteObligationChangeAttestationTRPCInput
  ): Promise<DeleteObligationChangeAttestationCompatibilityResult> => {
    if (trpcEnabled) {
      return trpcMutation.deleteObligationChangeAttestation(variables);
    }

    // The legacy Hasura action needs UserId, derived from the authenticated
    // user (the server derives it on the tRPC path).
    const result = await deleteAttestationGraphQL({
      variables: {
        object: {
          ObligationChangeId: variables.ObligationChangeId,
          UserId: user!.userId,
        },
      },
    });
    if (!result.data) {
      throw new Error('Failed to delete obligation change attestation');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      deleteObligationChangeAttestation,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    deleteObligationChangeAttestation,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
